import { Injectable } from '@angular/core';
import {
    IReclassificationAccountRef,
    IReclassificationTransaction,
    ReclassificationPeriod,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';
import { BehaviorSubject, Observable } from 'rxjs';
import { keyOf, refsEqual } from '../helpers/reclassificationAccountKey';
import {
    findConflicts,
    getIncomingTransactions,
    getOutgoingTransactions,
    getReclassSumForRef,
} from '../helpers/reclassificationEngine';

type AccountLike = { accountNumber: string; name: string };

/**
 * Draft entry while the user edits the reclassification modal.
 * `id` is set when the entry corresponds to a previously-saved transaction.
 */
export interface NewEntryDraft {
    id?: string;
    target: IReclassificationAccountRef;
    amount: number;
    description?: string;
}

/**
 * Page-scoped store for reclassification transactions on a single annual
 * report. Initialise from `groupingAndMasking.reclassifications` when the
 * grouping page loads; snapshot back into the AR on save.
 *
 * NOT providedIn: 'root' — provide at the grouping-masking page level so
 * navigating away discards state.
 */
@Injectable()
export class ReclassificationStoreService {
    private readonly subject = new BehaviorSubject<IReclassificationTransaction[]>([]);
    readonly transactions$: Observable<IReclassificationTransaction[]> = this.subject.asObservable();

    /** Load the AR's persisted transactions into the store. */
    init(transactions: IReclassificationTransaction[] | undefined | null): void {
        this.subject.next(Array.isArray(transactions) ? [...transactions] : []);
    }

    /** Read the current value synchronously. */
    snapshot(): IReclassificationTransaction[] {
        return this.subject.getValue();
    }

    /** Hand off the current transactions list to the grouping-masking save flow. */
    snapshotForSave(): IReclassificationTransaction[] {
        return this.subject.getValue().map((t) => ({ ...t }));
    }

    /**
     * Replace every transaction whose `(source, period)` equals the given
     * `(sourceRef, period)` with the modal's edited drafts. Drafts with
     * `id` are preserved (so their identity survives Save); new drafts get
     * fresh UUIDs.
     */
    upsertForSource(
        sourceRef: IReclassificationAccountRef,
        period: ReclassificationPeriod,
        statement: ReclassificationStatement,
        drafts: NewEntryDraft[],
    ): void {
        const current = this.subject.getValue();
        const survivors = current.filter((t) => !(t.period === period && refsEqual(t.source, sourceRef)));
        const next: IReclassificationTransaction[] = [
            ...survivors,
            ...drafts.map<IReclassificationTransaction>((d) => ({
                id: d.id ?? ReclassificationStoreService.generateId(),
                source: { ...sourceRef },
                target: { ...d.target },
                amount: d.amount,
                period,
                statement,
                description: d.description,
            })),
        ];
        this.subject.next(next);
    }

    /** Delete a single transaction by id. */
    delete(id: string): void {
        const current = this.subject.getValue();
        const next = current.filter((t) => t.id !== id);
        if (next.length !== current.length) this.subject.next(next);
    }

    /** Delete every transaction touching the given account (either leg, both periods). */
    deleteByAccount(ref: IReclassificationAccountRef): void {
        const current = this.subject.getValue();
        const next = current.filter((t) => !refsEqual(t.source, ref) && !refsEqual(t.target, ref));
        if (next.length !== current.length) this.subject.next(next);
    }

    /** Rewrite every transaction's source ref from `oldRef` to `newRef`. Both periods. */
    reassignSource(oldRef: IReclassificationAccountRef, newRef: IReclassificationAccountRef): void {
        const current = this.subject.getValue();
        let mutated = false;
        const next = current.map((t) => {
            if (refsEqual(t.source, oldRef)) {
                mutated = true;
                return { ...t, source: { ...newRef } };
            }
            return t;
        });
        if (mutated) this.subject.next(next);
    }

    /** Rewrite every transaction's target ref from `oldRef` to `newRef`. Both periods. */
    reassignTarget(oldRef: IReclassificationAccountRef, newRef: IReclassificationAccountRef): void {
        const current = this.subject.getValue();
        let mutated = false;
        const next = current.map((t) => {
            if (refsEqual(t.target, oldRef)) {
                mutated = true;
                return { ...t, target: { ...newRef } };
            }
            return t;
        });
        if (mutated) this.subject.next(next);
    }

    /**
     * Reassign every transaction (source or target) referencing `oldRef` to
     * `newRef`. Used by the conflict-resolution flow where the user picks a
     * replacement account.
     */
    reassignAnyLeg(oldRef: IReclassificationAccountRef, newRef: IReclassificationAccountRef): void {
        const current = this.subject.getValue();
        let mutated = false;
        const next = current.map((t) => {
            let changed: IReclassificationTransaction | null = null;
            if (refsEqual(t.source, oldRef)) {
                changed = { ...(changed ?? t), source: { ...newRef } };
            }
            if (refsEqual(t.target, oldRef)) {
                changed = { ...(changed ?? t), target: { ...newRef } };
            }
            if (changed) {
                mutated = true;
                return changed;
            }
            return t;
        });
        if (mutated) this.subject.next(next);
    }

    getForSource(ref: IReclassificationAccountRef, period: ReclassificationPeriod): IReclassificationTransaction[] {
        return getOutgoingTransactions(this.subject.getValue(), ref, period);
    }

    getForTarget(ref: IReclassificationAccountRef, period: ReclassificationPeriod): IReclassificationTransaction[] {
        return getIncomingTransactions(this.subject.getValue(), ref, period);
    }

    netSum(ref: IReclassificationAccountRef, period: ReclassificationPeriod): number {
        return getReclassSumForRef(this.subject.getValue(), ref, period);
    }

    /** Conflicts in the current store, given a per-statement account list. */
    computeConflicts(
        accountsByStatement: Record<ReclassificationStatement, AccountLike[]>,
    ): IReclassificationTransaction[] {
        return findConflicts(this.subject.getValue(), accountsByStatement);
    }

    /**
     * UUID-ish id generator. Uses crypto.randomUUID when available, otherwise a
     * fallback for older browsers. The id only needs to be unique within the
     * report — globally unique would be nicer but is not required.
     */
    private static generateId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return 'r-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    /** Internal helper for debugging tests. */
    debugKeyOf(ref: IReclassificationAccountRef): string {
        return keyOf(ref);
    }
}
